import type { NormalizedPoint } from '../model';
import type { PaperSize } from '../domain/geometry';
import { normalizedToPixel } from '../domain/bonds';
import { shapeExtentPoints, type PaperShape } from '../domain/objectEditing';
import type { RenderableAnnotation } from '../domain/annotationRendering';
import { atomEndGaps, curvedArrowPrimitives, type AnnotationPoint } from './annotationGeometry';
import { reactionArrowPrimitives } from './reactionArrowGeometry';
import { estimateTextWidth, layoutText, type TextMeasure } from './textLayout';

// Coordinates named by a primitive: polyline/polygon points, and every x,y pair of a path's `d`
// (a Bezier lies inside its control polygon, so its control points bound it).
export function primitivePoints(primitive: {
  readonly t: string;
  readonly points?: readonly AnnotationPoint[];
  readonly d?: string;
}): AnnotationPoint[] {
  if (primitive.points) return [...primitive.points];
  const numbers = (primitive.d ?? '').match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi)?.map(Number) ?? [];
  const points: AnnotationPoint[] = [];
  for (let i = 0; i + 1 < numbers.length; i += 2) points.push({ x: numbers[i]!, y: numbers[i + 1]! });
  return points;
}

// #637 -- points (paper-normalized) whose hull covers everything an annotation draws: a text
// block's box, a shape's full (rotated) extent, an arrow's actual geometry -- curve bulge and
// overlays included. Box/lasso selection encloses an annotation when it encloses all of them
// (accepted S2: whole objects, fully enclosed).
export function annotationExtentPoints(
  annotation: RenderableAnnotation,
  size: PaperSize,
  measure: TextMeasure = estimateTextWidth
): NormalizedPoint[] {
  const toNormalized = (point: AnnotationPoint): NormalizedPoint => ({
    x: point.x / size.width,
    y: point.y / size.height
  });
  if (annotation.kind === 'text') {
    const origin = normalizedToPixel(annotation.position, size);
    const { width, height } = layoutText(annotation.paragraphs, origin, measure);
    return [
      annotation.position,
      toNormalized({ x: origin.x + width, y: origin.y }),
      toNormalized({ x: origin.x, y: origin.y + height }),
      toNormalized({ x: origin.x + width, y: origin.y + height })
    ];
  }
  if (annotation.kind === 'shape') return [...shapeExtentPoints(annotation as PaperShape, size)];
  const from = normalizedToPixel(annotation.from, size);
  const to = normalizedToPixel(annotation.to, size);
  const primitives =
    annotation.kind === 'reaction-arrow'
      ? reactionArrowPrimitives(from, to, annotation.arrowKind, annotation.overlay, 1)
      : curvedArrowPrimitives(
          from,
          to,
          annotation.head,
          annotation.bow,
          1,
          1,
          annotation.controls?.map((point) => normalizedToPixel(point, size)) as
            [AnnotationPoint, AnnotationPoint] | undefined,
          atomEndGaps(annotation.fromAnchor, annotation.toAnchor)
        );
  return [from, to, ...primitives.flatMap(primitivePoints)].map(toNormalized);
}
